import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { rcLogger } from '@readcloud/common';
import { Button, Card, Modal } from '@readcloud/component-library';
import { MediaAnnotation } from '@readcloud/data';
import { AspectRatio, P } from '@readcloud/ui';
import React from 'react';
import isEqual from 'react-fast-compare';
import ReactPlayer from 'react-player';
import { useModalState } from '../../hooks/useModalState';
import { InteractionPreventer } from '../InteractionDisabler';
import { AnnotationRendererProps } from '../types';
import {
  YouTubeProxyEmbed,
  extractYouTubeId,
} from '../../Components/YouTubeProxyEmbed';

const iconStyle = {
  height: '4rem',
  width: '4rem',
};

const cardStyle = {
  height: '100%',
  width: '100%',
  transition: 'box-shadow 100ms ease-out',
  overflow: 'hidden',
} as const;

export const MediaAnnotationViewer = function MediaAnnotationViewer({
  height,
  width,
  id,
  data,
  dragging,
  editing,
}: AnnotationRendererProps<MediaAnnotation['data'], MediaAnnotation['type']>) {
  const { anchor, open, hide, show } = useModalState();

  const handleError = React.useCallback((e: any) => {
    rcLogger.log(e);
  }, []);

  const isAudio = data.url?.toLowerCase().match(/\.(wav|mp3|m4a)$/) !== null;
  const youtubeId = extractYouTubeId(data.url);

  const playerEl = youtubeId ? (
    <YouTubeProxyEmbed
      videoId={youtubeId}
      className="rc-pointer-events-auto rc-touch-auto"
    />
  ) : (
    <ReactPlayer
      className="rc-flex rc-pointer-events-auto rc-touch-auto"
      height="100%"
      width="100%"
      url={data.url}
      controls
      onError={handleError}
      pip={false}
      config={{
        file: {
          forceAudio: isAudio,
          attributes: {
            controlsList: 'nodownload',
            disablePictureInPicture: true,
          },
        },
      }}
    />
  );

  return (
    <Card key="inner" style={cardStyle} className="inheritTransform">
      {data.variant == 'Icon' ? (
        <div className="rc-flex rc-justify-center rc-items-center rc-w-full rc-h-full">
          <Modal
            isOpen={open}
            toggle={hide}
            contentClassName="rc-overflow-hidden"
          >
            {isAudio ? (
              <div className="rc-w-full rc-h-12">{playerEl}</div>
            ) : (
              <AspectRatio ratio={16 / 9}>{playerEl}</AspectRatio>
            )}
          </Modal>
          <Button
            ref={anchor as React.RefObject<HTMLButtonElement>}
            onClick={show}
            className="rc-pointer-events-auto rc-touch-auto rc-flex rc-flex-col rc-items-center rc-justify-center rc-h-full rc-w-full rc-gap-2"
          >
            <FontAwesomeIcon icon={'circle-play'} />
            <span className="rc-text-nowrap">Media</span>
          </Button>
        </div>
      ) : isAudio ? (
        playerEl
      ) : (
        <AspectRatio
          ratio={16 / 9}
          className="rc-flex rc-justify-center rc-items-center"
        >
          {playerEl}
        </AspectRatio>
      )}
      <InteractionPreventer enabled={editing} />
    </Card>
  );
};
